/**
 * Structural shape of a scope photo. Every identity field is optional - the
 * function probes them in priority order. Index signature keeps it permissive
 * for callers passing richer photo records.
 */
export interface ScopePhoto {
    id?: unknown;
    blobId?: unknown;
    s3Key?: unknown;
    fileMetadataId?: unknown;
    dataUrl?: unknown;
    url?: unknown;
    thumbnailUrl?: unknown;
    name?: unknown;
    [key: string]: unknown;
}

/**
 * Structural shape of a scope item w.r.t. photo-assignment authority. All
 * fields optional + an index signature so callers passing full scope items
 * (which carry many more fields) don't need to narrow.
 */
export interface ScopeAuthorityItem {
    photoAssignmentsEditedBy?: unknown;
    photoAssignmentsEditedAt?: unknown;
    photoAssignmentsLocked?: unknown;
    photoAssignmentReviewReceipt?: unknown;
    images?: unknown;
    [key: string]: unknown;
}

export function scopePhotoIdentity(photo: ScopePhoto | null | undefined): string {
    if (!photo) return '';
    return String(
        photo.id
        || (photo.blobId ? `blob:${photo.blobId}` : '')
        || (photo.s3Key ? `s3:${photo.s3Key}` : '')
        || (photo.fileMetadataId ? `file:${photo.fileMetadataId}` : '')
        || (photo.dataUrl ? 'data-url' : '')
        || (photo.url ? `url:${photo.url}` : '')
        || (photo.thumbnailUrl ? `thumb:${photo.thumbnailUrl}` : '')
        || (photo.name ? `name:${photo.name}` : '')
    ).trim();
}

/** Stable per-position key for disposition receipts, even if legacy photos share an identity. */
export function scopePhotoDispositionKey(photo: ScopePhoto | null | undefined, index: number): string {
    return `${scopePhotoIdentity(photo) || 'photo'}#${Number.isInteger(index) ? index : 0}`;
}

export function scopePhotoFingerprint(images: ScopePhoto[] | null | undefined = []): string {
    if (!Array.isArray(images)) return '';
    return images
        .map((photo, index) => scopePhotoIdentity(photo) || `idx:${index}`)
        .join('|');
}

export function markScopePhotoAssignmentsEdited(item: ScopeAuthorityItem = {}, source: string = 'user', editedAt: string = new Date().toISOString()): ScopeAuthorityItem {
    return {
        ...item,
        photoAssignmentsEditedAt: editedAt,
        photoAssignmentsEditedBy: source,
    };
}

export function hasPhotoAssignmentAuthority(item: ScopeAuthorityItem | null | undefined = {}): boolean {
    return !!(
        item
        && typeof item === 'object'
        && (
            item.photoAssignmentsEditedBy === 'user'
            || item.photoAssignmentsEditedAt
            || item.photoAssignmentsLocked === true
        )
    );
}

const photoAssignmentStampMs = (item: ScopeAuthorityItem | null | undefined): number => {
    const raw = item?.photoAssignmentsEditedAt;
    const parsed = raw ? Date.parse(String(raw)) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Resolve the item that owns the current photo selection. Valid edit stamps
 * provide cross-surface last-write-wins ordering, including an intentional
 * empty selection. Equal/absent stamps retain the conservative legacy rule:
 * explicit live Stage 4 authority or nonempty live photos win over the modal.
 */
export function chooseAuthoritativeScopePhotoItem(
    { quoteItem = {}, liveItem = {} }: { quoteItem?: ScopeAuthorityItem; liveItem?: ScopeAuthorityItem } = {},
): ScopeAuthorityItem {
    const quoteEditedAt = photoAssignmentStampMs(quoteItem);
    const liveEditedAt = photoAssignmentStampMs(liveItem);

    if (quoteEditedAt > liveEditedAt) return quoteItem;
    if (liveEditedAt > quoteEditedAt) return liveItem;
    if (liveEditedAt > 0) return liveItem;

    if (hasPhotoAssignmentAuthority(liveItem)) return liveItem;
    if (Array.isArray(liveItem?.images) && liveItem.images.length > 0) return liveItem;
    if (hasPhotoAssignmentAuthority(quoteItem)) return quoteItem;
    if (Array.isArray(quoteItem?.images)) return quoteItem;
    return liveItem;
}

export function chooseAuthoritativeScopeImages(
    { quoteItem = {}, liveItem = {} }: { quoteItem?: ScopeAuthorityItem; liveItem?: ScopeAuthorityItem } = {},
): unknown[] {
    if (hasPhotoAssignmentAuthority(liveItem)) {
        return Array.isArray(liveItem.images) ? liveItem.images : [];
    }
    if (Array.isArray(liveItem?.images) && liveItem.images.length > 0) {
        return liveItem.images;
    }
    if (Array.isArray(quoteItem?.images)) {
        return quoteItem.images;
    }
    return Array.isArray(liveItem?.images) ? liveItem.images : [];
}
